/**
 * F17 T17.1.6 v3 Commit 6 — zakładka "Wszystkie aktywne" (instance-level,
 * cross-user, gated permission).
 *
 * Endpoint: `GET /api/processes/instances/all` (Commit 4). Wymaga
 * permission `processes.decisions.view_all` — backend zwraca 403 dla
 * niewystarczających uprawnień. Tab łapie 403 i pokazuje fallback
 * "permissionDenied" (bez dodatkowego frontend permission hooka).
 *
 * Etap 6 (frontend): endpoint jest company-required (`X-Company-Id`, backend
 * 400 MISSING_HEADER bez niego) — w przeciwieństwie do `/my-pending-decisions`
 * i `/my-submitted-instances` (cross-company, bez requireCompany). W trybie
 * "Wszystkie firmy" krótko spinamy zapytanie i pokazujemy łagodny placeholder
 * zamiast wysyłać żądanie skazane na 400.
 *
 * Filtry per kolumna (Kreator Listy, popup w nagłówku `InstanceListingTable`) —
 * server-side przez `?headerFilters=` (mirror `KsefInvoices.tsx`/`ProcessesListPage.tsx`).
 * Zmiana filtra resetuje `offset` do 0 (mirror `RecordTablePicker.tsx`) — inaczej user
 * mógłby wylądować na pustej stronie po zawężeniu wyniku. Whitelist backendu →
 * `HEADER_FILTER_FIELDS` w `apps/api/app/api/processes/instances/all/route.ts`.
 *
 * Sort server-side (kontrolowany, trzecia fala kokpitu decyzji) — klik nagłówka wysyła
 * `?sort=<pole>:<kier>` (whitelist `SORT_FIELDS` w tym samym route.ts) i resetuje `offset`
 * do 0, identycznie jak zmiana filtra. Stan startowy (`sortColumn=null`) NIE wysyła
 * `?sort=` — domyślna kolejność (`submittedAt desc`) zostaje nietknięta do pierwszego kliku.
 *
 * Grupowanie (fala 2 kokpitu decyzji, `?groupBy=`, biała lista `ACTIVE_INSTANCE_GROUP_BY`
 * z `@saas/shared` — proces/zgłaszający/kartoteka/status) — LOKALNY stan (`groupByValue`),
 * mirror `MySubmittedInstancesTab.tsx`. `groupedQuery` dociąga WYŁĄCZNIE nagłówki koszyków
 * (`status: 'active'` wpięte tak samo jak w trybie płaskim — ekran pokazuje TYLKO instancje
 * w toku). Koszyki startują ZWINIĘTE; klik nagłówka dociąga wiersze TEGO koszyka leniwie —
 * osobne żądanie `fetchActiveInstanceGroupRows` per rozwinięty klucz, z WŁASNĄ mini-paginacją.
 * Mechanika (stan rozwinięcia/paginacji, `useQueries`, mapowanie na kształt
 * `ConfigurableTable.groups`) żyje we WSPÓLNYM haku `useLazyGroupRows`
 * (`./use-lazy-group-rows.tsx`) — mirror `MyPendingDecisionsTab.tsx`/
 * `MySubmittedInstancesTab.tsx`.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACTIVE_INSTANCE_GROUP_BY, type DecisionGroupBy } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import type { ColumnHeaderFilters } from '@/components/configurable-table';
import { Dropdown } from '@/components/Dropdown';
import {
  type DecisionGroupHeader,
  fetchActiveInstanceGroupRows,
  fetchAllInstances,
  fetchGroupedAllInstances,
  type InstanceListingRow,
} from '@/lib/processes/approvals-api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

import {
  buildInstanceListingColumns,
  InstanceListingTable,
  serializeHeaderFiltersForRequest,
} from './InstanceListingTable';
import { PaginationControls } from './PaginationControls';
import { useLazyGroupRows } from './use-lazy-group-rows';

const PAGE_LIMIT = 50;

export function ActiveInstancesTab() {
  const { t } = useTranslation();
  const { isAll } = useActiveCompanyScope();
  const [offset, setOffset] = useState(0);
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Grupowanie (fala 2) — `''` = bez grupowania (dotychczasowe zachowanie).
  const [groupByValue, setGroupByValue] = useState<DecisionGroupBy | ''>('');
  const groupBy: DecisionGroupBy | null = groupByValue || null;
  const isGrouped = groupBy !== null;

  const columns = useMemo(() => buildInstanceListingColumns(true, t), [t]);
  const serializedHeaderFilters = useMemo(
    () => serializeHeaderFiltersForRequest(headerFilters, columns),
    [headerFilters, columns],
  );
  const sortParam = sortColumn ? `${sortColumn}:${sortDirection}` : undefined;

  function handleHeaderFiltersChange(next: ColumnHeaderFilters) {
    setHeaderFilters(next);
    setOffset(0);
  }

  // Sort server-side (kontrolowany) — reset offset (zbiór stron się zmienia).
  function handleSort(column: string, direction: 'asc' | 'desc') {
    setSortColumn(column);
    setSortDirection(direction);
    setOffset(0);
  }

  const flatQuery = useQuery({
    queryKey: [
      'processes',
      'instances',
      'all',
      {
        offset,
        limit: PAGE_LIMIT,
        headerFilters: serializedHeaderFilters,
        sort: sortParam ?? null,
      },
    ],
    // Zakładka „Aktywne" pokazuje TYLKO instancje w toku (`status='active'`) — spójnie
    // z SellCall (osobny widok aktywnych) oraz z licznikiem kafelka. Terminalne
    // (zakończone/anulowane/błąd) tu nie wchodzą.
    queryFn: () =>
      fetchAllInstances({
        status: 'active',
        limit: PAGE_LIMIT,
        offset,
        headerFilters: serializedHeaderFilters,
        sort: sortParam,
      }),
    enabled: !isAll && !isGrouped,
    // HTTP errors (4xx/5xx) — nie retry'ujemy. Backend zwrócił sensowny status,
    // ponowienie nic nie zmieni. Network errors (`err.response` brakuje, np.
    // timeout / DNS) — 1 retry. 403 obsługujemy specjalnie w isError branchu.
    retry: (failureCount, err) => {
      if (err instanceof AxiosError && err.response) return false;
      return failureCount < 1;
    },
  });

  const groupedQuery = useQuery({
    queryKey: [
      'processes',
      'instances',
      'all',
      'grouped',
      { groupBy, headerFilters: serializedHeaderFilters },
    ],
    queryFn: () => {
      // Strażnik TS — nieosiągalny w runtime (`enabled` gwarantuje `groupBy !== null`).
      if (groupBy === null) throw new Error('groupedQuery wywołane bez groupBy');
      return fetchGroupedAllInstances({
        groupBy,
        status: 'active',
        headerFilters: serializedHeaderFilters,
      });
    },
    enabled: !isAll && isGrouped,
    retry: (failureCount, err) => {
      if (err instanceof AxiosError && err.response) return false;
      return failureCount < 1;
    },
  });

  const groups: DecisionGroupHeader[] = groupedQuery.data?.groups ?? [];

  // Wiersze rozwiniętych koszyków — LENIWE doładowanie (fala 2), wyciągnięte do WSPÓLNEGO
  // haka `useLazyGroupRows` (mirror `MyPendingDecisionsTab.tsx`/`MySubmittedInstancesTab.tsx`).
  // `queryKeyExtra` MUSI mirror'ować dokładnie to, co `fetchRows` czyta z domknięcia
  // (`serializedHeaderFilters`) — inaczej cache react-query się rozjedzie. `status: 'active'`
  // jest STAŁY (ekran pokazuje tylko instancje w toku) — nie wchodzi do `queryKeyExtra`.
  // Ten ekran nie ma kolumny „Wartość" — `buildSummary` pominięte (`summary: null`).
  const { expandedGroupKeys, toggleGroupExpand, resetExpansion, mappedGroups } =
    useLazyGroupRows<InstanceListingRow>({
      groupBy,
      groups,
      enabled: !isAll && isGrouped,
      queryKeyBase: ['processes', 'instances', 'all', 'group-rows'],
      queryKeyExtra: { headerFilters: serializedHeaderFilters, sort: sortParam ?? null },
      fetchRows: ({ groupKey, offset: rowsOffset, limit: rowsLimit }) => {
        if (groupBy === null) throw new Error('fetchRows (group-rows) wywołane bez groupBy');
        return fetchActiveInstanceGroupRows({
          groupBy,
          groupKey,
          status: 'active',
          limit: rowsLimit,
          offset: rowsOffset,
          headerFilters: serializedHeaderFilters,
          sort: sortParam,
        });
      },
      testIdPrefix: 'active',
    });

  function handleGroupByChange(value: string) {
    setGroupByValue(value === '' ? '' : (value as DecisionGroupBy));
    setOffset(0);
    resetExpansion();
  }

  const groupByOptions = useMemo(
    () => [
      { value: '', label: t('tenant.processes.approvals.groupBy.none') },
      ...ACTIVE_INSTANCE_GROUP_BY.map((dim) => ({
        value: dim,
        label: t(`tenant.processes.approvals.groupBy.${dim}`),
      })),
    ],
    [t],
  );

  if (isAll) {
    return (
      <div className="text-sm text-text-tertiary" data-testid="approvals-pick-company">
        {t('tenant.processes.approvals.pickCompany')}
      </div>
    );
  }

  const isLoading = isGrouped ? groupedQuery.isLoading : flatQuery.isLoading;
  const isError = isGrouped ? groupedQuery.isError : flatQuery.isError;
  const currentError = isGrouped ? groupedQuery.error : flatQuery.error;

  if (isLoading) {
    return (
      <div className="text-sm text-text-tertiary" data-testid="approvals-loading">
        {t('tenant.processes.approvals.loading')}
      </div>
    );
  }

  if (isError) {
    const status = currentError instanceof AxiosError ? currentError.response?.status : undefined;
    if (status === 403) {
      return (
        <div className="text-sm text-text-tertiary" data-testid="approvals-permission-denied">
          {t('tenant.processes.approvals.permissionDenied')}
        </div>
      );
    }
    return (
      <div className="text-sm text-error" data-testid="approvals-load-error">
        {t('tenant.processes.approvals.loadError')}
      </div>
    );
  }

  const rows = flatQuery.data?.data ?? [];
  const total = flatQuery.data?.total ?? 0;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <InstanceListingTable
          rows={isGrouped ? [] : rows}
          showSubmitter={true}
          testIdPrefix="active"
          emptyTitle={t('tenant.processes.approvals.empty.active')}
          headerFilters={headerFilters}
          onHeaderFiltersChange={handleHeaderFiltersChange}
          sortColumn={sortColumn ?? undefined}
          sortDirection={sortDirection}
          onSort={handleSort}
          groups={mappedGroups}
          expandedGroupKeys={isGrouped ? expandedGroupKeys : undefined}
          onToggleGroup={isGrouped ? toggleGroupExpand : undefined}
          toolbarExtra={
            <div className="flex items-center gap-1.5">
              <span className="whitespace-nowrap text-sm text-text-tertiary">
                {t('tenant.processes.approvals.groupBy.label')}
              </span>
              <Dropdown
                options={groupByOptions}
                value={groupByValue}
                onChange={handleGroupByChange}
                aria-label={t('tenant.processes.approvals.groupBy.label')}
                className="w-48"
                data-testid="active-groupby-select"
              />
            </div>
          }
        />
      </div>
      {/* Paginacja (trybu płaskiego) — ukryta w trybie grupowanym (nagłówki koszyków są
          agregacją SQL bez paginacji; wiersze KAŻDEGO koszyka mają WŁASNĄ mini-paginację
          w jego `footer`, patrz `mappedGroups`). */}
      {!isGrouped && (
        <div className="shrink-0">
          <PaginationControls
            total={total}
            offset={offset}
            limit={PAGE_LIMIT}
            onOffsetChange={setOffset}
          />
        </div>
      )}
    </div>
  );
}
